import React, { useState, useEffect, useCallback } from 'react'
import { DropdownButton, InputGroup } from 'react-bootstrap'
import moment from 'moment'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import { DateRange } from 'react-date-range'

const DROPDOWN_ID = 'traffic-range-selector-dropdown'

export type TrafficCustomDateRange = { start: string; end: string }

type Props = {
  timePeriod: string
  customDateRange?: TrafficCustomDateRange
  disabled?: boolean
  onChange: (next: { timePeriod: string; customDateRange?: TrafficCustomDateRange }) => void
}

const MINUTE_PRESETS: { label: string; value: string }[] = [
  { label: 'Last 5 minutes', value: '5-minutes' },
  { label: 'Last 10 minutes', value: '10-minutes' },
  { label: 'Last 30 minutes', value: '30-minutes' },
  { label: 'Last 60 minutes', value: '60-minutes' },
  { label: 'Last 24 hours', value: '24-hours' },
]

const DAY_PRESET_BUTTONS: { label: string; period: string }[] = [
  { label: 'Today', period: 'today' },
  { label: 'Yesterday', period: 'yesterday' },
  { label: 'Last 7 days', period: '7-days' },
  { label: 'Last 14 days', period: '14-days' },
  { label: 'Last 30 days', period: '30-days' },
  { label: 'Last 90 days', period: '90-days' },
]

function periodToMomentValue(timePeriod: string, customDateRange?: TrafficCustomDateRange) {
  if (timePeriod === 'custom' && customDateRange?.start && customDateRange?.end) {
    return {
      start: moment(customDateRange.start),
      end: moment(customDateRange.end),
    }
  }
  const now = moment()
  if (timePeriod === 'today') return { start: now.clone().startOf('day'), end: now.clone() }
  if (timePeriod === 'yesterday') {
    return {
      start: now.clone().subtract(1, 'day').startOf('day'),
      end: now.clone().subtract(1, 'day').endOf('day'),
    }
  }
  const m = timePeriod.match(/^(\d+)-days$/)
  if (m) {
    const n = parseInt(m[1], 10)
    return {
      start: now.clone().startOf('day').subtract(n - 1, 'days'),
      end: now.clone(),
    }
  }
  return null
}

export default function TrafficDateFilter({ timePeriod, customDateRange, disabled, onChange }: Props) {
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [dates, setDates] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: 'selection',
    },
  ])
  const [startDate, setStartDate] = useState<Date | null>(null)

  const syncCalendarFromPeriod = useCallback(() => {
    const v = periodToMomentValue(timePeriod, customDateRange)
    if (v) {
      setDates([
        {
          startDate: v.start.toDate(),
          endDate: v.end.toDate(),
          key: 'selection',
        },
      ])
    }
  }, [timePeriod, customDateRange])

  useEffect(() => {
    if (dropdownOpen) {
      syncCalendarFromPeriod()
    }
  }, [dropdownOpen, syncCalendarFromPeriod])

  const showDropdown = () => {
    const el = document.getElementById(DROPDOWN_ID)
    if (el) {
      el.setAttribute('aria-expanded', 'true')
      el.classList.add('show')
    }
    setTimeout(() => {
      const menu = document.querySelector(`[aria-labelledby="${DROPDOWN_ID}"]`)
      menu?.classList.add('show')
      setDropdownOpen(true)
    }, 1)
  }

  const hideDropdown = () => {
    const el = document.getElementById(DROPDOWN_ID)
    if (el) {
      el.setAttribute('aria-expanded', 'false')
      el.classList.remove('show')
    }
    const menu = document.querySelector(`[aria-labelledby="${DROPDOWN_ID}"]`)
    menu?.classList.remove('show')
    setDropdownOpen(false)
    setStartDate(null)
  }

  const applyCustomRange = (start: Date, end: Date) => {
    onChange({
      timePeriod: 'custom',
      customDateRange: {
        start: moment(start).format('YYYY-MM-DD'),
        end: moment(end).format('YYYY-MM-DD'),
      },
    })
    setTimeout(() => hideDropdown(), 100)
  }

  const handleSetDates = (item: { selection: { startDate: Date; endDate: Date } }) => {
    const { startDate: s, endDate: e } = item.selection
    if (startDate === null) {
      setStartDate(s)
      setDates([{ startDate: s, endDate: e, key: 'selection' }])
    } else {
      setStartDate(null)
      const a = moment(s).isBefore(moment(e)) ? s : e
      const b = moment(s).isAfter(moment(e)) ? s : e
      applyCustomRange(a, b)
    }
  }

  const dropdownTitle = (() => {
    if (timePeriod === 'custom' && customDateRange?.start && customDateRange?.end) {
      return (
        <span className="flex-1 text-input-box" style={{ marginRight: 8 }}>
          <b style={{ color: 'white' }} className="primary-text">
            <span style={{ color: 'white' }} className="fa fa-calendar" />
            &nbsp;
            {moment(customDateRange.start).format('MMM D YYYY')}
            <b style={{ color: 'white', borderRadius: 0 }}> To </b>
            {moment(customDateRange.end).format('MMM D YYYY')}
          </b>
        </span>
      )
    }
    const preset = MINUTE_PRESETS.find((p) => p.value === timePeriod)
    const dayPreset = DAY_PRESET_BUTTONS.find((p) => p.period === timePeriod)
    const display = preset?.label || dayPreset?.label || timePeriod
    return (
      <span className="flex-1 text-input-box" style={{ marginRight: 8 }}>
        <b style={{ color: 'white' }} className="primary-text">
          <span style={{ color: 'white' }} className="fa fa-calendar" />
          &nbsp;
          {display}
        </b>
      </span>
    )
  })()

  const minDate = new Date(2018, 0, 1)
  const maxDate = new Date()

  return (
    <span className="v-center root-component traffic-date-filter" style={{ marginRight: 8, position: 'relative' }}>
      <InputGroup className="full-width flex-box">
        <DropdownButton
          title={dropdownTitle}
          id={DROPDOWN_ID}
          disabled={disabled}
          show={dropdownOpen}
          className="flex-box v-center"
          onToggle={() => {
            if (!dropdownOpen) {
              showDropdown()
            } else {
              setTimeout(() => hideDropdown(), 1)
            }
          }}
        >
          <div className="tw-px-4 tw-pt-2 tw-pb-1">
            <div className="tw-text-xs tw-font-semibold tw-text-slate-500 tw-mb-2">Recent activity</div>
            <div
              className="quick-selection"
              style={{
                width: '100%',
                display: 'flex',
                flexWrap: 'wrap',
              }}
            >
              {MINUTE_PRESETS.map(({ label, value }) => (
                <div
                  key={value}
                  style={{ width: '50%', display: 'flex', justifyContent: 'center', marginBottom: 8 }}
                >
                  <div
                    style={{
                      width: '80%',
                      backgroundColor: timePeriod === value ? '#0d6efd' : '#F5F5F5',
                      borderRadius: 4,
                      textAlign: 'center',
                      color: timePeriod === value ? 'white' : '#333',
                      cursor: 'pointer',
                      padding: '6px 4px',
                      fontSize: 12,
                    }}
                    onClick={() => {
                      onChange({ timePeriod: value })
                      hideDropdown()
                    }}
                  >
                    {label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ marginTop: -4, display: 'flex', justifyContent: 'center' }}>
            <DateRange
              onChange={handleSetDates}
              moveRangeOnFirstSelection={false}
              ranges={dates}
              minDate={minDate}
              maxDate={maxDate}
            />
          </div>

          <div className="tw-px-4 tw-pb-2">
            <div className="tw-text-xs tw-font-semibold tw-text-slate-500 tw-mb-2">Day ranges</div>
            <div
              className="quick-selection"
              style={{ width: '100%', display: 'flex', flexWrap: 'wrap' }}
            >
              {DAY_PRESET_BUTTONS.map(({ label, period }) => (
                <div
                  key={period}
                  style={{ width: '50%', display: 'flex', justifyContent: 'center', marginBottom: 8 }}
                >
                  <div
                    style={{
                      width: '80%',
                      backgroundColor: timePeriod === period ? '#0d6efd' : '#F5F5F5',
                      borderRadius: 4,
                      textAlign: 'center',
                      color: timePeriod === period ? 'white' : '#333',
                      cursor: 'pointer',
                      padding: '6px 4px',
                      fontSize: 12,
                    }}
                    onClick={() => {
                      onChange({ timePeriod: period })
                      hideDropdown()
                    }}
                  >
                    {label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="tw-px-4 tw-pb-3 tw-text-xs tw-text-slate-500">
            Or select a custom range on the calendar above (click start date, then end date).
          </div>
        </DropdownButton>
      </InputGroup>
    </span>
  )
}
